import React, { useEffect, useMemo, useState } from 'react';
import { useClassroomContext } from 'contexts/ClassroomContext';
import { useNavigate } from 'react-router-dom';
import teacherService from 'services/teacherService';
import { SearchBar } from 'view/components/common/SearchBar';
import Assignment from 'models/Assignment';
import { Filter, FilterOption } from 'view/components/common/Filter';
import { AppPage } from 'view/components/common/AppPage';
import SidebarButton from 'view/components/buttons/SidebarButton';
import AssignmentSubmissions from 'view/components/teacher/classroom/AssignmentSubmissions';
import Modal from 'view/components/common/Modal';
import AssignmentSubmission from 'models/AssignmentSubmission';
import useApiCall from 'contexts/ApiCall';
import AppSidebar from 'view/components/common/Sidebar';
import contentService from 'services/contentService';
import AssignmentTypeForm from 'view/components/teacher/assignment/AssignmentTypeForm';
import {
  faArchive,
  faBookOpen,
  faChartLine,
  faPlusSquare,
} from '@fortawesome/free-solid-svg-icons';
import TeacherAssignmentList from 'view/components/teacher/classroom/TeacherAssignmentList';
import PanelDropdown from 'view/components/common/PanelDropdown';
import { dateOrNull } from 'utils/dateUtils';
import TeacherEvaluationList from 'view/components/teacher/classroom/TeacherEvaluationList';
import Evaluation from 'models/Evaluation';
import EvaluationSubmissions from 'view/components/teacher/classroom/EvaluationSubmissions';
import { useGradebook } from 'contexts/TeacherGradebookContext';

import 'view/style/teacher/classroom.css';

const ClassroomAssignments: React.FC = () => {
  const { classroom } = useClassroomContext();
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment>();
  const [selectedEvaluation, setSelectedEvaluation] = useState<Evaluation>();
  const { gradebook, setGradebook } = useGradebook();
  const [averagesDisplay, setAveragesDisplay] = useState<FilterOption[]>(
    contentService.getTeacherConstants().display_averages,
  );
  const [averageDisplay, setAverageDisplay] = useState<FilterOption>(
    contentService.getTeacherConstants().display_averages[0],
  );
  const [viewAssignmentSubmissions, setViewAssignmentSubmissions] =
    useState<boolean>(false);
  const [viewEvaluationSubmissions, setViewEvaluationSubmissions] =
    useState<boolean>(false);
  const [viewAddAssignment, setViewAddAssignment] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const makeApiCall = useApiCall();
  const navigate = useNavigate();

  useEffect(() => {
    if (gradebook || !classroom) return;
    makeApiCall(teacherService.getGradebook, classroom.id)
      .then((respGradebook) => setGradebook(respGradebook))
      .catch((error) => alert(error.message));
  }, [gradebook, classroom, makeApiCall, setGradebook]);

  const handleUpdateAssignmentSubmission = (
    assignmentSubmission: AssignmentSubmission,
    field: string,
    value: any,
  ) => {
    const updatedAssignmentSubmission =
      AssignmentSubmission.fromServerAssignmentSubmission({
        ...assignmentSubmission,
        [field]: value,
      });
    makeApiCall(
      teacherService.updateAssignmentSubmission,
      updatedAssignmentSubmission,
    )
      .then((respAssignment) => {
        setGradebook((prevGradebook) =>
          prevGradebook?.updateAssignmentSubmission(respAssignment.submission),
        );
      })
      .catch((error) => alert(error.message));
  };

  const handleBack = () => {
    if (!classroom) return;
    if (selectedAssignment) {
      setSelectedAssignment(undefined);
      return;
    }
    navigate(`/teacher/classroom/${classroom.id}`);
  };

  const handleCreateAssignment = () => {
    setViewAddAssignment(true);
  };

  const handleEditAssignment = () => {
    if (!classroom || !selectedAssignment) return;
    navigate(`${selectedAssignment.getId()}/`);
  };

  const handleAverageDisplayChange = (
    checked: boolean,
    displayOption: FilterOption,
  ) => {
    setAveragesDisplay((prev) => {
      const updatedDisplayOptions = prev.map((option) =>
        option === displayOption
          ? { ...option, is_active: checked }
          : { ...option, is_active: false },
      );
      return updatedDisplayOptions;
    });
    if (checked) setAverageDisplay(displayOption);
  };

  const handleViewAssignmentSubmissions = (
    event: React.MouseEvent<HTMLDivElement>,
    assignment: Assignment,
  ) => {
    event.stopPropagation();
    event.preventDefault();

    setViewAssignmentSubmissions(true);
    setSelectedAssignment(assignment);
  };

  const handleViewEvaluationSubmissions = (
    event: React.MouseEvent<HTMLDivElement>,
    evaluation: Evaluation,
  ) => {
    event.stopPropagation();
    event.preventDefault();

    setViewEvaluationSubmissions(true);
    setSelectedEvaluation(evaluation);
  };

  const handleSelectEvaluation = (evaluation: Evaluation) =>
    setSelectedEvaluation(evaluation);

  const displayAssignments = useMemo(
    () =>
      gradebook?.assignments.filter((a) =>
        a.title.toLowerCase().includes(searchTerm),
      ) ?? [],
    [gradebook?.assignments, searchTerm],
  );

  const openAssignments = useMemo(
    () =>
      displayAssignments.filter((a) => {
        const dueDate = dateOrNull(a.due_date);
        const now = new Date();

        return !dueDate || dueDate < now;
      }),
    [displayAssignments],
  );

  const pastAssignments = useMemo(
    () =>
      displayAssignments.filter((a) => {
        const dueDate = dateOrNull(a.due_date);
        const now = new Date();

        return dueDate && dueDate >= now;
      }),
    [displayAssignments],
  );

  const handleSelectAssignment = (assignment: Assignment) =>
    setSelectedAssignment(assignment);

  if (!gradebook) return <AppPage />;

  return (
    <AppPage>
      <div className="app-page-content">
        <AppSidebar onExit={handleBack}>
          <>
            <SearchBar onSearch={setSearchTerm} />
            <Filter
              type="onechoice"
              label="Averages"
              options={averagesDisplay}
              onOptionChange={handleAverageDisplayChange}
            />
            {selectedAssignment && gradebook && (
              <>
                <div className="assignment-detail-header">
                  <label className="label-medium">
                    {selectedAssignment.title}
                  </label>
                </div>
                <div className="assignment-details-list">
                  <div className="assignment-detail">
                    <label className="label-small">Reading Group</label>
                    <label className="label-small">
                      {`${selectedAssignment.min_reading_level} - ${selectedAssignment.max_reading_level}`}
                    </label>
                  </div>
                  <div className="assignment-detail">
                    <label className="label-small">Completion</label>
                    <label className="label-small">
                      {
                        gradebook.averages.assignments[
                          selectedAssignment.getId()
                        ].completion_score
                      }
                      %
                    </label>
                  </div>
                  <div className="assignment-detail">
                    <label className="label-small">Grade</label>
                    <label className="label-small">
                      {
                        gradebook.averages.assignments[
                          selectedAssignment.getId()
                        ].grade
                      }
                      %
                    </label>
                  </div>
                  <div className="assignment-detail">
                    <label className="label-small">Accuracy</label>
                    <label className="label-small">
                      {
                        gradebook.averages.assignments[
                          selectedAssignment.getId()
                        ].correctness_score
                      }
                      %
                    </label>
                  </div>
                  <div className="assignment-detail">
                    <label className="label-small">Key Word Accuracy</label>
                    <label className="label-small">
                      {
                        gradebook.averages.assignments[
                          selectedAssignment.getId()
                        ].key_word_accuracy_score
                      }
                      %
                    </label>
                  </div>
                  <div className="assignment-detail">
                    <label className="label-small">Completion Time</label>
                    <label className="label-small">
                      {
                        gradebook.averages.assignments[
                          selectedAssignment.getId()
                        ].completion_time
                      }
                      %
                    </label>
                  </div>
                </div>
              </>
            )}
            <SidebarButton
              type="go"
              onClick={handleCreateAssignment}
              fontAwesomeIcon={faPlusSquare}
              label="New assignment"
            />
          </>
        </AppSidebar>
        <div className="app-main-content">
          <h1>{classroom?.name} Assignments</h1>
          <div className="item-list">
            <PanelDropdown
              icon={faBookOpen}
              label={`Open assignments (${openAssignments.length})`}
              startExpanded={true}
            >
              <TeacherAssignmentList
                assignments={openAssignments}
                selectedAssignment={selectedAssignment}
                averages={gradebook.averages}
                averageDisplay={averageDisplay}
                onEnterAssignment={handleEditAssignment}
                onSelectAssignment={handleSelectAssignment}
                onViewSubmissions={handleViewAssignmentSubmissions}
              />
            </PanelDropdown>
            <PanelDropdown
              icon={faChartLine}
              label={`Evaluations (${gradebook.evaluations.assigned.length})`}
              startExpanded={false}
            >
              <TeacherEvaluationList
                evaluations={gradebook.evaluations.assigned}
                selectedEvaluation={selectedEvaluation}
                averages={gradebook.averages}
                averageDisplay={averageDisplay}
                onEnter={() => {}}
                onSelect={handleSelectEvaluation}
                onViewSubmissions={handleViewEvaluationSubmissions}
              />
            </PanelDropdown>
            <PanelDropdown
              icon={faArchive}
              label={`Past assignments (${pastAssignments.length})`}
              startExpanded={false}
            >
              <TeacherAssignmentList
                assignments={pastAssignments}
                selectedAssignment={selectedAssignment}
                averages={gradebook.averages}
                averageDisplay={averageDisplay}
                onEnterAssignment={handleEditAssignment}
                onSelectAssignment={handleSelectAssignment}
                onViewSubmissions={handleViewAssignmentSubmissions}
              />
            </PanelDropdown>
          </div>
        </div>
      </div>
      <Modal
        isOpen={viewAddAssignment}
        onClose={() => setViewAddAssignment(false)}
      >
        <AssignmentTypeForm />
      </Modal>
      {selectedAssignment && (
        <Modal
          isOpen={viewAssignmentSubmissions}
          onClose={() => setViewAssignmentSubmissions(false)}
        >
          <AssignmentSubmissions
            assignment={selectedAssignment}
            gradebook={gradebook}
            onClose={() => {
              setViewAssignmentSubmissions(false);
              setSelectedAssignment(undefined);
            }}
            onUpdateAssignmentSubmission={handleUpdateAssignmentSubmission}
          />
        </Modal>
      )}
      <Modal
        isOpen={viewEvaluationSubmissions}
        onClose={() => setViewEvaluationSubmissions(false)}
      >
        <EvaluationSubmissions
          evaluation={selectedEvaluation!}
          gradebook={gradebook}
          onUpdateEvaluationSubmission={() => {}}
          onClose={() => setViewEvaluationSubmissions(false)}
        />
      </Modal>
    </AppPage>
  );
};

export default ClassroomAssignments;
